import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Grid, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Chip, Button, Alert,
  Card, CardContent, Divider, IconButton, Tooltip,
  TableSortLabel, TablePagination,
} from '@mui/material';
import {
  Refresh, Close as CloseIcon, AccountBalanceWallet, Delete, ExitToApp, Download,
} from '@mui/icons-material';
import axios from 'axios';
import { exportToExcel, fmtDateIST, fmtNum } from '../utils/exportExcel';

const IST_FMT = {
  timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short',
  hour: '2-digit', minute: '2-digit', hour12: true,
};
function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
  return new Date(s).toLocaleString('en-IN', IST_FMT);
}
function fmt(v) { return v != null ? Number(v).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'; }

const PAGE_OPTS = [10, 20, 50, 100, -1];

function SourceChip({ source }) {
  const isAuto = source === 'auto';
  return (
    <Chip
      label={isAuto ? 'Auto' : 'Manual'}
      size="small"
      sx={{
        fontSize: '0.65rem', fontWeight: 700, height: 20,
        bgcolor: isAuto ? 'rgba(156,39,176,0.1)' : 'rgba(33,150,243,0.1)',
        color: isAuto ? '#9C27B0' : '#2196F3',
        border: `1px solid ${isAuto ? '#9C27B0' : '#2196F3'}`,
      }}
    />
  );
}

export default function PaperPortfolio({ apiUrl }) {
  const [positions, setPositions] = useState([]);
  const [totalPositions, setTotalPositions] = useState(0);
  const [orders, setOrders] = useState([]);
  const [totalOrders, setTotalOrders] = useState(0);
  const [portfolio, setPortfolio] = useState(null);
  const [pnlSummary, setPnlSummary] = useState({ unrealized: 0, realized: 0 });
  const [loading, setLoading] = useState(false);
  const [closing, setClosing] = useState(null);
  const [exporting, setExporting] = useState(false);

  const [posOrder, setPosOrder] = useState('desc');
  const [posOrderBy, setPosOrderBy] = useState('created_at');
  const [posPage, setPosPage] = useState(0);
  const [posRows, setPosRows] = useState(20);

  const [ordOrder, setOrdOrder] = useState('desc');
  const [ordOrderBy, setOrdOrderBy] = useState('created_at');
  const [ordPage, setOrdPage] = useState(0);
  const [ordRows, setOrdRows] = useState(20);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchPnl = useCallback(async () => {
    try {
      const [pnlRes, portRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/pnl-summary`, { headers, params: { trade_mode: 'paper' } }),
        axios.get(`${apiUrl}/orders/portfolio`, { headers }),
      ]);
      setPnlSummary(pnlRes.data);
      setPortfolio(portRes.data);
    } catch (err) { console.error(err); }
  }, [apiUrl]);

  const fetchPositions = useCallback(async () => {
    try {
      const params = {
        skip: posPage * posRows,
        limit: posRows === -1 ? 1000 : posRows,
        sort_by: posOrderBy, sort_order: posOrder,
        trade_mode: 'paper', status: 'open',
      };
      const res = await axios.get(`${apiUrl}/orders/positions`, { headers, params });
      setPositions(res.data.items || []);
      setTotalPositions(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [apiUrl, posPage, posRows, posOrderBy, posOrder]);

  const fetchOrders = useCallback(async () => {
    try {
      const params = {
        skip: ordPage * ordRows,
        limit: ordRows === -1 ? 1000 : ordRows,
        sort_by: ordOrderBy, sort_order: ordOrder,
        trade_mode: 'paper',
      };
      const res = await axios.get(`${apiUrl}/orders/`, { headers, params });
      setOrders(res.data.items || []);
      setTotalOrders(res.data.total || 0);
    } catch (err) { console.error(err); }
  }, [apiUrl, ordPage, ordRows, ordOrderBy, ordOrder]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchPnl(), fetchPositions(), fetchOrders()]);
    setLoading(false);
  }, [fetchPnl, fetchPositions, fetchOrders]);

  useEffect(() => { fetchPnl(); }, [fetchPnl]);
  useEffect(() => { fetchPositions(); }, [fetchPositions]);
  useEffect(() => { fetchOrders(); }, [fetchOrders]);

  // Auto-refresh positions and P&L every 1 minute
  useEffect(() => {
    const timer = setInterval(() => {
      fetchPositions();
      fetchPnl();
    }, 60000);
    return () => clearInterval(timer);
  }, [fetchPositions, fetchPnl]);

  const closePosition = async (id) => {
    setClosing(id);
    try {
      await axios.post(`${apiUrl}/orders/positions/${id}/close`, {}, { headers });
      fetchAll();
    } catch (err) { console.error(err); }
    finally { setClosing(null); }
  };

  const deletePosition = async (id) => {
    if (!window.confirm('Delete this position permanently?')) return;
    try {
      await axios.delete(`${apiUrl}/orders/positions/${id}`, { headers });
      fetchAll();
    } catch (err) { console.error(err); }
  };

  const deleteOrder = async (orderId) => {
    try {
      await axios.delete(`${apiUrl}/orders/${orderId}`, { headers });
      fetchOrders();
      fetchPnl();
    } catch (err) { console.error(err); }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const [posRes, ordRes] = await Promise.all([
        axios.get(`${apiUrl}/orders/positions`, { headers, params: { trade_mode: 'paper', status: 'open', limit: 10000, sort_by: 'created_at', sort_order: 'desc' } }),
        axios.get(`${apiUrl}/orders/`, { headers, params: { trade_mode: 'paper', limit: 10000, sort_by: 'created_at', sort_order: 'desc' } }),
      ]);
      const allPos = posRes.data.items || [];
      const allOrd = ordRes.data.items || [];

      const sheets = [
        {
          name: 'Summary',
          headers: ['Field', 'Value'],
          rows: [
            ['Export Date (IST)', fmtDateIST(new Date().toISOString())],
            ['Trade Mode', 'Paper'],
            ['Total Capital', fmtNum(portfolio?.total_capital)],
            ['Used Margin', fmtNum(portfolio?.used_margin)],
            ['Unrealized P&L', fmtNum(pnlSummary.unrealized)],
            ['Realized P&L', fmtNum(pnlSummary.realized)],
            ['Open Positions', allPos.length],
            ['Total Orders', allOrd.length],
          ],
          colWidths: [28, 20],
        },
        {
          name: 'Open Positions',
          headers: ['Symbol', 'Direction', 'Strategy', 'Qty', 'Entry Price', 'Current Price', 'Unrealized P&L', 'Stop Loss', 'Take Profit', 'Source', 'Opened (IST)'],
          rows: allPos.map(p => [
            p.symbol, (p.order_type || 'BUY') === 'SELL' ? 'Short' : 'Long',
            p.strategy_name || '', p.quantity,
            fmtNum(p.entry_price), fmtNum(p.current_price),
            fmtNum(p.unrealized_pnl), fmtNum(p.stop_loss), fmtNum(p.take_profit),
            p.source, fmtDateIST(p.created_at),
          ]),
        },
        {
          name: 'Orders',
          headers: ['Date Time (IST)', 'Strategy', 'Symbol', 'Exchange', 'Type', 'Qty', 'Price', 'Status', 'P&L', 'Source'],
          rows: allOrd.map(o => [
            fmtDateIST(o.created_at), o.strategy_name || '', o.symbol, o.exchange || '',
            o.order_type, o.quantity, fmtNum(o.entry_price),
            o.status, fmtNum(o.profit_loss), o.source,
          ]),
        },
      ];
      exportToExcel(`Paper_Portfolio_${new Date().toISOString().slice(0, 10)}`, sheets);
    } catch (err) { console.error(err); }
    finally { setExporting(false); }
  };

  const directionLabel = (otype) => {
    const t = (otype || 'BUY').toUpperCase();
    return t === 'SELL' ? 'Short' : 'Long';
  };
  const dirColor = (otype) => (otype || 'BUY').toUpperCase() === 'SELL' ? '#E8392E' : '#4CAF50';
  const handlePosSort = (col) => { setPosOrder(posOrderBy === col && posOrder === 'asc' ? 'desc' : 'asc'); setPosOrderBy(col); setPosPage(0); };
  const handleOrdSort = (col) => { setOrdOrder(ordOrderBy === col && ordOrder === 'asc' ? 'desc' : 'asc'); setOrdOrderBy(col); setOrdPage(0); };

  return (
    <Box data-testid="paper-portfolio-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <AccountBalanceWallet sx={{ color: '#3A5BA0' }} />
          <Typography variant="h5">Paper Portfolio</Typography>
          <Chip label="VIRTUAL" size="small" sx={{ bgcolor: '#3A5BA0', color: '#fff', fontWeight: 700 }} />
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="outlined" size="small" startIcon={<Download />} onClick={handleExport}
            disabled={exporting} data-testid="export-paper-portfolio-btn">
            {exporting ? 'Exporting...' : 'Export Excel'}
          </Button>
          <Button variant="outlined" size="small" startIcon={<Refresh />} onClick={fetchAll} disabled={loading}
            data-testid="refresh-paper-btn">Refresh</Button>
        </Box>
      </Box>

      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Capital', value: fmt(portfolio?.total_capital || 1000000), color: '#3A5BA0' },
          { label: 'Margin', value: fmt((portfolio?.total_capital || 1000000) - (portfolio?.used_margin || 0)), color: '#4A7EC8' },
          { label: 'Open', value: totalPositions, color: '#3A5BA0' },
          { label: 'Unrealized', value: fmt(pnlSummary.unrealized), color: pnlSummary.unrealized >= 0 ? '#4CAF50' : '#E8392E' },
          { label: 'Realized', value: fmt(pnlSummary.realized), color: pnlSummary.realized >= 0 ? '#4CAF50' : '#E8392E' },
        ].map((c, i) => (
          <Grid item xs={6} sm={2.4} key={i}>
            <Card sx={{ borderLeft: `3px solid ${c.color}` }}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{c.label}</Typography>
                <Typography variant="body1" fontWeight={700} sx={{ color: c.color }}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Open Positions */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(58,91,160,0.06)' }}>
          <Typography variant="subtitle1" fontWeight={700}>Open Positions ({totalPositions})</Typography>
        </Box>
        <Divider />
        {positions.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No open paper positions.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'order_type', label: 'Dir' },
                      { id: 'source', label: 'Source' },
                      { id: 'strategy_name', label: 'Strategy' },
                      { id: 'quantity', label: 'Qty', align: 'right' },
                      { id: 'entry_price', label: 'Entry', align: 'right' },
                      { id: 'current_price', label: 'Current', align: 'right' },
                      { id: 'unrealized_pnl', label: 'P&L', align: 'right' },
                      { id: 'created_at', label: 'Opened' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={posOrderBy === col.id}
                          direction={posOrderBy === col.id ? posOrder : 'asc'}
                          onClick={() => handlePosSort(col.id)}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {positions.map(p => {
                    const pnl = p.unrealized_pnl || 0;
                    return (
                      <TableRow key={p.id} data-testid={`pos-row-${p.id}`}>
                        <TableCell><Typography variant="body2" fontWeight={600}>{p.symbol}</Typography></TableCell>
                        <TableCell>
                          <Chip label={directionLabel(p.order_type)} size="small" sx={{
                            fontSize: '0.6rem', fontWeight: 700, height: 18,
                            bgcolor: `${dirColor(p.order_type)}18`, color: dirColor(p.order_type),
                            border: `1px solid ${dirColor(p.order_type)}`,
                          }} />
                        </TableCell>
                        <TableCell><SourceChip source={p.source} /></TableCell>
                        <TableCell>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {p.strategy_name || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">{p.quantity}</TableCell>
                        <TableCell align="right">{fmt(p.entry_price)}</TableCell>
                        <TableCell align="right">{fmt(p.current_price)}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={700} color={pnl >= 0 ? 'success.main' : 'error.main'}>
                            {pnl >= 0 ? '+' : ''}{fmt(pnl)}
                          </Typography>
                        </TableCell>
                        <TableCell><Typography variant="caption">{toIST(p.created_at)}</Typography></TableCell>
                        <TableCell align="center">
                          <Box display="flex" gap={0.3} justifyContent="center">
                            <Tooltip title="Exit / Close Position">
                              <IconButton size="small" color="warning" onClick={() => closePosition(p.id)}
                                disabled={closing === p.id} data-testid={`exit-pos-${p.id}`}>
                                <ExitToApp sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete Position">
                              <IconButton size="small" color="error" onClick={() => deletePosition(p.id)}
                                data-testid={`delete-pos-${p.id}`}>
                                <Delete sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={totalPositions} rowsPerPage={posRows} page={posPage}
              onPageChange={(e, p) => setPosPage(p)}
              onRowsPerPageChange={(e) => { setPosRows(parseInt(e.target.value)); setPosPage(0); }} />
          </>
        )}
      </Paper>

      {/* Recent Paper Orders */}
      <Paper>
        <Box sx={{ px: 2, py: 1, bgcolor: 'rgba(58,91,160,0.06)' }}>
          <Typography variant="subtitle1" fontWeight={700}>Recent Paper Orders ({totalOrders})</Typography>
        </Box>
        <Divider />
        {orders.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No paper orders yet.</Alert>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {[
                      { id: 'created_at', label: 'Time' },
                      { id: 'source', label: 'Source' },
                      { id: 'strategy_name', label: 'Strategy' },
                      { id: 'symbol', label: 'Symbol' },
                      { id: 'order_type', label: 'Type' },
                      { id: 'quantity', label: 'Qty', align: 'right' },
                      { id: 'entry_price', label: 'Price', align: 'right' },
                      { id: 'status', label: 'Status' },
                      { id: 'profit_loss', label: 'P&L', align: 'right' },
                    ].map(col => (
                      <TableCell key={col.id} align={col.align}>
                        <TableSortLabel active={ordOrderBy === col.id}
                          direction={ordOrderBy === col.id ? ordOrder : 'asc'}
                          onClick={() => handleOrdSort(col.id)}>{col.label}</TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell align="center"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map(o => (
                    <TableRow key={o.id} data-testid={`order-row-${o.id}`}>
                      <TableCell><Typography variant="caption">{toIST(o.created_at)}</Typography></TableCell>
                      <TableCell><SourceChip source={o.source} /></TableCell>
                      <TableCell><Typography variant="caption" color="primary" fontWeight={600}>{o.strategy_name || '-'}</Typography></TableCell>
                      <TableCell><Typography fontWeight={600}>{o.symbol}</Typography></TableCell>
                      <TableCell>
                        <Chip label={o.order_type} size="small"
                          color={o.order_type === 'BUY' ? 'success' : o.order_type === 'SELL' ? 'error' : o.order_type === 'EXIT' ? 'warning' : 'default'} />
                      </TableCell>
                      <TableCell align="right">{o.quantity}</TableCell>
                      <TableCell align="right">{fmt(o.entry_price)}</TableCell>
                      <TableCell><Chip label={o.status} size="small" /></TableCell>
                      <TableCell align="right">
                        <Typography fontWeight={700} color={(o.profit_loss || 0) >= 0 ? 'success.main' : 'error.main'}>
                          {o.profit_loss != null ? fmt(o.profit_loss) : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title="Delete order">
                          <IconButton size="small" color="error" onClick={() => deleteOrder(o.id)}
                            data-testid={`delete-paper-order-${o.id}`}>
                            <Delete sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
              component="div" count={totalOrders} rowsPerPage={ordRows} page={ordPage}
              onPageChange={(e, p) => setOrdPage(p)}
              onRowsPerPageChange={(e) => { setOrdRows(parseInt(e.target.value)); setOrdPage(0); }} />
          </>
        )}
      </Paper>
    </Box>
  );
}
